const LEAFLET_VERSION = '1.9.4';
const LEAFLET_CSS = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.css`;
const LEAFLET_JS = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.js`;

let leafletPromise;

function loadLeaflet() {
  if (window.L) return Promise.resolve(window.L);
  if (leafletPromise) return leafletPromise;

  leafletPromise = new Promise((resolve, reject) => {
    if (!document.querySelector('link[data-necksofa-leaflet]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = LEAFLET_CSS;
      link.dataset.necksofaLeaflet = '';
      document.head.appendChild(link);
    }

    const existing = document.querySelector('script[data-necksofa-leaflet]');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.L), { once: true });
      existing.addEventListener('error', reject, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = LEAFLET_JS;
    script.defer = true;
    script.dataset.necksofaLeaflet = '';
    script.addEventListener('load', () => resolve(window.L), { once: true });
    script.addEventListener('error', reject, { once: true });
    document.head.appendChild(script);
  });

  return leafletPromise;
}

function debounce(fn, wait = 150) {
  let timer;
  return (...args) => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => fn(...args), wait);
  };
}

function normalize(value = '') {
  return String(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .trim();
}

function haversineMiles(a, b) {
  const R = 3958.7613;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);

  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;

  return 2 * R * Math.asin(Math.sqrt(h));
}

function directionsUrl(dealer) {
  const params = new URLSearchParams({
    api: '1',
    destination: `${dealer.lat},${dealer.lng}`,
  });
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

function createPopupNode(dealer, directionsLabel) {
  const wrap = document.createElement('div');
  wrap.className = 'dealer-locator__popup';

  const title = document.createElement('strong');
  title.className = 'dealer-locator__popup-title';
  title.textContent = dealer.name;

  const address = document.createElement('div');
  address.className = 'dealer-locator__popup-address';
  address.textContent = `${dealer.address}, ${dealer.country}`;

  const link = document.createElement('a');
  link.className = 'dealer-locator__popup-link';
  link.href = directionsUrl(dealer);
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.textContent = directionsLabel;

  wrap.append(title, address);

  if (dealer.phone) {
    const phone = document.createElement('div');
    phone.className = 'dealer-locator__popup-phone';
    phone.textContent = dealer.phone;
    wrap.appendChild(phone);
  }

  if (dealer.website) {
    const website = document.createElement('a');
    website.className = 'dealer-locator__popup-link';
    website.href = dealer.website;
    website.target = '_blank';
    website.rel = 'noopener noreferrer';
    website.textContent = 'Website';
    wrap.appendChild(website);
  }

  wrap.appendChild(link);
  return wrap;
}

function buildAddress(dealer) {
  if (dealer.address) return dealer.address;

  return [
    dealer.address_1,
    dealer.address_2,
    dealer.city,
    dealer.region,
    dealer.postal_code,
  ]
    .filter(Boolean)
    .join(', ');
}

function normalizeDealer(raw) {
  const lat = Number.parseFloat(raw.lat ?? raw.latitude);
  const lng = Number.parseFloat(raw.lng ?? raw.longitude);

  if (!raw.name || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  return {
    ...raw,
    id: raw.id ?? `dealer-${lat}-${lng}`,
    name: String(raw.name).trim(),
    address: buildAddress(raw),
    country: String(raw.country || '').trim(),
    country_code: String(raw.country_code || '').trim().toUpperCase(),
    lat,
    lng,
    phone: String(raw.phone || '').trim(),
    website: String(raw.website || '').trim(),
  };
}

class DealerLocator extends HTMLElement {
  connectedCallback() {
    if (this._connected) return;
    this._connected = true;

    this.source = this.dataset.source;
    this.initialCount = Number(this.dataset.initialCount || 12);
    this.batchSize = Number(this.dataset.batchSize || 12);
    this.mapZoom = Number(this.dataset.mapZoom || 4);
    this.directionsLabel = this.dataset.directionsLabel || 'Get directions';

    this.searchInput = this.querySelector('[data-dealer-search]');
    this.countrySelect = this.querySelector('[data-dealer-country]');
    this.locateButton = this.querySelector('[data-dealer-locate]');
    this.clearButton = this.querySelector('[data-dealer-clear]');
    this.results = this.querySelector('[data-dealer-results]');
    this.resultCount = this.querySelector('[data-dealer-count]');
    this.loadMoreButton = this.querySelector('[data-dealer-more]');
    this.mapElement = this.querySelector('[data-dealer-map]');
    this.status = this.querySelector('[data-dealer-status]');

    this.visibleCount = this.initialCount;
    this.userLocation = null;
    this.userMarker = null;
    this.markers = new Map();
    this.abortController = new AbortController();

    this.onSearch = debounce(() => {
      this.visibleCount = this.initialCount;
      this.applyFilters(true);
    });

    this.searchInput?.addEventListener('input', this.onSearch, {
      signal: this.abortController.signal,
    });

    this.countrySelect?.addEventListener(
      'change',
      () => {
        this.visibleCount = this.initialCount;
        this.applyFilters(true);
      },
      { signal: this.abortController.signal }
    );

    this.locateButton?.addEventListener(
      'click',
      () => this.locateUser(),
      { signal: this.abortController.signal }
    );

    this.clearButton?.addEventListener(
      'click',
      () => this.clearFilters(),
      { signal: this.abortController.signal }
    );

    this.loadMoreButton?.addEventListener(
      'click',
      () => {
        this.visibleCount += this.batchSize;
        this.renderResults();
      },
      { signal: this.abortController.signal }
    );

    this.init();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.map?.remove();
    this._connected = false;
  }

  async init() {
    this.setStatus(this.dataset.loadingLabel || 'Loading dealers…');

    try {
      const [L, response] = await Promise.all([
        loadLeaflet(),
        fetch(this.source, { credentials: 'same-origin' }),
      ]);

      if (!response.ok) throw new Error(`Dealer data request failed: ${response.status}`);

      this.L = L;

      const databaseDealers = await response.json();
      const extraDealers = this.readExtraLocations();

      this.dealers = [...databaseDealers, ...extraDealers]
        .map(normalizeDealer)
        .filter(Boolean);

      this.initMap();
      this.addMarkers();
      this.populateCountries();
      this.filteredDealers = [...this.dealers];
      this.renderResults();
      this.fitAllDealers();
      this.setStatus('');
      this.setAttribute('data-ready', '');
    } catch (error) {
      console.error('[Dealer locator]', error);
      this.setStatus(this.dataset.errorLabel || 'Unable to load dealer locations.');
      this.setAttribute('data-error', '');
    }
  }

  readExtraLocations() {
    const node = this.querySelector('[data-dealer-extra-locations]');
    if (!node) return [];

    try {
      const parsed = JSON.parse(node.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('[Dealer locator] Additional location data is invalid:', error);
      return [];
    }
  }

  initMap() {
    const L = this.L;

    this.map = L.map(this.mapElement, {
      scrollWheelZoom: false,
      zoomControl: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors',
    }).addTo(this.map);
  }

  addMarkers() {
    const L = this.L;

    this.dealers.forEach((dealer) => {
      const marker = L.marker([dealer.lat, dealer.lng]);
      marker.bindPopup(createPopupNode(dealer, this.directionsLabel));

      marker.on('click', () => {
        this.highlightCard(dealer.id);
      });

      marker.addTo(this.map);
      this.markers.set(dealer.id, marker);
    });
  }

  populateCountries() {
    if (!this.countrySelect) return;

    const current = this.countrySelect.value;
    const countries = [...new Set(this.dealers.map((dealer) => dealer.country))].sort();

    countries.forEach((country) => {
      const option = document.createElement('option');
      option.value = country;
      option.textContent = country;
      this.countrySelect.appendChild(option);
    });

    this.countrySelect.value = current;
  }

  applyFilters(updateMap = false) {
    const query = normalize(this.searchInput?.value);
    const country = this.countrySelect?.value || '';

    let dealers = this.dealers.filter((dealer) => {
      if (country && dealer.country !== country) return false;

      if (!query) return true;

      const haystack = normalize(
        `${dealer.name} ${dealer.address} ${dealer.city || ''} ${dealer.region || ''} ${dealer.postal_code || ''} ${dealer.country} ${dealer.country_code}`
      );

      return haystack.includes(query);
    });

    if (this.userLocation) {
      dealers = dealers
        .map((dealer) => ({
          ...dealer,
          distance: haversineMiles(this.userLocation, dealer),
        }))
        .sort((a, b) => a.distance - b.distance);
    }

    this.filteredDealers = dealers;
    this.renderResults();

    if (updateMap) this.fitFilteredDealers();
  }

  renderResults() {
    if (!this.results || !this.filteredDealers) return;

    this.results.replaceChildren();

    const total = this.filteredDealers.length;
    const visible = this.filteredDealers.slice(0, this.visibleCount);

    if (this.resultCount) {
      this.resultCount.textContent =
        total === 1
          ? `1 ${this.dataset.resultSingular || 'dealer'}`
          : `${total} ${this.dataset.resultPlural || 'dealers'}`;
    }

    if (total === 0) {
      const empty = document.createElement('div');
      empty.className = 'dealer-locator__empty';
      empty.textContent = this.dataset.noResultsLabel || 'No dealers found.';
      this.results.appendChild(empty);

      if (this.loadMoreButton) this.loadMoreButton.hidden = true;
      return;
    }

    const fragment = document.createDocumentFragment();

    visible.forEach((dealer) => {
      fragment.appendChild(this.createCard(dealer));
    });

    this.results.appendChild(fragment);

    if (this.loadMoreButton) {
      this.loadMoreButton.hidden = visible.length >= total;
    }
  }

  createCard(dealer) {
    const card = document.createElement('article');
    card.className = 'dealer-locator__card';
    card.dataset.dealerId = String(dealer.id);

    const button = document.createElement('button');
    button.className = 'dealer-locator__card-main';
    button.type = 'button';

    const title = document.createElement('h3');
    title.className = 'dealer-locator__card-title';
    title.textContent = dealer.name;

    const address = document.createElement('p');
    address.className = 'dealer-locator__card-address';
    address.textContent = dealer.address;

    const country = document.createElement('p');
    country.className = 'dealer-locator__card-country';
    country.textContent = dealer.country;

    button.append(title, address, country);

    if (dealer.phone) {
      const phone = document.createElement('p');
      phone.className = 'dealer-locator__card-phone';
      phone.textContent = dealer.phone;
      button.appendChild(phone);
    }

    if (Number.isFinite(dealer.distance)) {
      const distance = document.createElement('span');
      distance.className = 'dealer-locator__distance';
      distance.textContent = `${dealer.distance.toFixed(
        dealer.distance < 10 ? 1 : 0
      )} mi`;
      button.appendChild(distance);
    }

    button.addEventListener(
      'click',
      () => this.focusDealer(dealer),
      { signal: this.abortController.signal }
    );

    const actions = document.createElement('div');
    actions.className = 'dealer-locator__card-actions';

    const directions = document.createElement('a');
    directions.className = 'dealer-locator__directions';
    directions.href = directionsUrl(dealer);
    directions.target = '_blank';
    directions.rel = 'noopener noreferrer';
    directions.textContent = this.directionsLabel;

    actions.appendChild(directions);

    if (dealer.website) {
      const website = document.createElement('a');
      website.className = 'dealer-locator__website';
      website.href = dealer.website;
      website.target = '_blank';
      website.rel = 'noopener noreferrer';
      website.textContent = 'Website';
      actions.appendChild(website);
    }

    card.append(button, actions);

    return card;
  }

  focusDealer(dealer) {
    const marker = this.markers.get(dealer.id);
    if (!marker) return;

    this.map.flyTo([dealer.lat, dealer.lng], Math.max(this.map.getZoom(), 12), {
      duration: 0.65,
    });

    marker.openPopup();
    this.highlightCard(dealer.id);

    if (window.matchMedia('(max-width: 749px)').matches) {
      this.mapElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  highlightCard(id) {
    this.results
      ?.querySelectorAll('.dealer-locator__card[data-active]')
      .forEach((card) => card.removeAttribute('data-active'));

    const card = this.results?.querySelector(
      `.dealer-locator__card[data-dealer-id="${CSS.escape(String(id))}"]`
    );

    if (card) card.setAttribute('data-active', '');
  }

  fitAllDealers() {
    if (!this.dealers?.length) return;
    const bounds = this.L.latLngBounds(
      this.dealers.map((dealer) => [dealer.lat, dealer.lng])
    );
    this.map.fitBounds(bounds, { padding: [30, 30], maxZoom: this.mapZoom });
  }

  fitFilteredDealers() {
    const dealers = this.filteredDealers;

    this.markers.forEach((marker, id) => {
      const isVisible = dealers.some((dealer) => dealer.id === id);
      if (isVisible && !this.map.hasLayer(marker)) marker.addTo(this.map);
      if (!isVisible && this.map.hasLayer(marker)) this.map.removeLayer(marker);
    });

    if (!dealers.length) return;

    if (dealers.length === 1) {
      const dealer = dealers[0];
      this.map.flyTo([dealer.lat, dealer.lng], 12, { duration: 0.6 });
      return;
    }

    const bounds = this.L.latLngBounds(
      dealers.map((dealer) => [dealer.lat, dealer.lng])
    );

    this.map.fitBounds(bounds, { padding: [30, 30], maxZoom: 10 });
  }

  locateUser() {
    if (!navigator.geolocation) {
      this.setStatus(this.dataset.locationUnsupported || 'Location is not supported by this browser.');
      return;
    }

    this.locateButton?.setAttribute('aria-busy', 'true');
    this.setStatus(this.dataset.locatingLabel || 'Finding your location…');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.locateButton?.removeAttribute('aria-busy');

        this.userLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };

        if (this.userMarker) this.userMarker.remove();

        this.userMarker = this.L.circleMarker(
          [this.userLocation.lat, this.userLocation.lng],
          {
            radius: 7,
            weight: 3,
            fillOpacity: 1,
          }
        )
          .bindTooltip(this.dataset.myLocationLabel || 'My location')
          .addTo(this.map);

        this.visibleCount = this.initialCount;
        this.applyFilters(false);

        const nearest = this.filteredDealers.slice(0, Math.min(10, this.filteredDealers.length));
        const coordinates = [
          [this.userLocation.lat, this.userLocation.lng],
          ...nearest.map((dealer) => [dealer.lat, dealer.lng]),
        ];

        if (coordinates.length > 1) {
          this.map.fitBounds(this.L.latLngBounds(coordinates), {
            padding: [35, 35],
            maxZoom: 10,
          });
        } else {
          this.map.setView([this.userLocation.lat, this.userLocation.lng], 10);
        }

        this.setStatus(this.dataset.nearestLabel || 'Dealers are sorted by distance.');
      },
      (error) => {
        console.warn('[Dealer locator] Geolocation error:', error);
        this.locateButton?.removeAttribute('aria-busy');
        this.setStatus(
          this.dataset.locationError || 'We could not access your location. Please search by city, state, or ZIP code.'
        );
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000,
      }
    );
  }

  clearFilters() {
    if (this.searchInput) this.searchInput.value = '';
    if (this.countrySelect) this.countrySelect.value = '';

    this.userLocation = null;
    this.userMarker?.remove();
    this.userMarker = null;
    this.visibleCount = this.initialCount;

    this.filteredDealers = [...this.dealers];

    this.markers.forEach((marker) => {
      if (!this.map.hasLayer(marker)) marker.addTo(this.map);
    });

    this.renderResults();
    this.fitAllDealers();
    this.setStatus('');
  }

  setStatus(message) {
    if (this.status) this.status.textContent = message;
  }
}

if (!customElements.get('dealer-locator')) {
  customElements.define('dealer-locator', DealerLocator);
}
